/**
 * Account menu (top bar, PM efb0e798): the signed-in person's avatar with a
 * pop-up holding Profile (name, email, password and the avatar upload),
 * What's New (staff), language (families), the Club Central platform tools
 * and manager link (platform staff only), Logout, and the Club Central
 * attribution with the build version.
 *
 * It was the sidebar's identity footer (2026-06 nav refresh); it moved to
 * the top bar with the Accelimate-style shell. Light/dark lives in the
 * top bar's Display panel.
 */
import React, { useEffect, useRef, useState } from "react";
import { Building2, ChevronDown, ExternalLink, LogOut, Sparkles, User as UserIcon, Wrench } from "lucide-react";
import { getCardBackground, getLightBorder, getMutedText, getPrimaryText } from "../../../utils/themeUtils";

interface AccountMenuProps {
  name: string;
  roleLabel: string;
  avatarUrl?: string;
  isDarkMode: boolean;
  onOpenProfile: () => void;
  onLogout: () => void;
  isPlatformAdmin: boolean;
  /** Club Central platform staff: opens the platform tools drawer. Omit to hide. */
  onOpenPlatformTools?: () => void;
  /** families get the language picker in the menu */
  languagePicker?: React.ReactNode;
  /** staff/admin: opens the What's New changelog modal. Omit to hide. */
  onOpenWhatsNew?: () => void;
  /** show an unseen dot next to the What's New item */
  whatsNewUnseen?: boolean;
  versionLabel?: string;
  versionTitle?: string;
}

const AccountMenu: React.FC<AccountMenuProps> = ({
  name,
  roleLabel,
  avatarUrl,
  isDarkMode,
  onOpenProfile,
  onLogout,
  isPlatformAdmin,
  onOpenPlatformTools,
  languagePicker,
  onOpenWhatsNew,
  whatsNewUnseen,
  versionLabel,
  versionTitle,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const initials =
    (name || "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("") || "?";

  const avatar = avatarUrl ? (
    <img src={avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
  ) : (
    <span className="w-8 h-8 rounded-full bg-gray-500/20 text-xs font-semibold flex items-center justify-center flex-shrink-0">
      {initials}
    </span>
  );

  const menuItemCls = `flex items-center gap-2 w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${getPrimaryText(
    isDarkMode
  )} ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`;

  return (
    <div ref={menuRef} className="relative flex-shrink-0">
      <button
        type="button"
        className={`flex items-center gap-2 rounded-full p-1 lg:pr-2 transition-colors ${
          isDarkMode ? "hover:bg-white/10" : "hover:bg-black/5"
        }`}
        onClick={() => setMenuOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label="Account menu"
        title={name || "Account"}
      >
        {avatar}
        {/* Name over role from lg up; on smaller screens the avatar alone. */}
        <span className="hidden lg:block min-w-0 max-w-[10rem] text-left">
          <span className="block text-sm font-semibold leading-tight truncate">{name || "Account"}</span>
          <span className="block text-xs leading-tight opacity-80 truncate">{roleLabel}</span>
        </span>
        <ChevronDown className="hidden lg:block w-4 h-4 opacity-80" aria-hidden="true" />
      </button>

      {menuOpen && (
        <div
          className={`absolute right-0 top-full mt-2 z-[70] w-64 max-w-[calc(100vw-1rem)] rounded-lg shadow-xl border p-1.5 ${getCardBackground(
            isDarkMode
          )} ${getLightBorder(isDarkMode)}`}
          role="menu"
        >
          <div className="px-3 pt-1.5 pb-2 lg:hidden">
            <div className={`text-sm font-semibold truncate ${getPrimaryText(isDarkMode)}`}>{name || "Account"}</div>
            <div className={`text-xs truncate ${getMutedText(isDarkMode)}`}>{roleLabel}</div>
          </div>
          <button className={menuItemCls} role="menuitem" onClick={() => { setMenuOpen(false); onOpenProfile(); }}>
            <UserIcon className="w-4 h-4" />
            Profile
          </button>
          {onOpenWhatsNew && (
            <button
              className={menuItemCls}
              role="menuitem"
              onClick={() => { setMenuOpen(false); onOpenWhatsNew(); }}
            >
              <Sparkles className="w-4 h-4" />
              What's New
              {whatsNewUnseen && (
                <span className="ml-auto w-2 h-2 rounded-full bg-sky-400" aria-label="unseen updates" />
              )}
            </button>
          )}
          {languagePicker && <div className="px-3 py-2">{languagePicker}</div>}
          {(onOpenPlatformTools || isPlatformAdmin) && (
            <div className={`my-1 border-t ${getLightBorder(isDarkMode)}`} />
          )}
          {onOpenPlatformTools && (
            <button
              className={menuItemCls}
              role="menuitem"
              onClick={() => { setMenuOpen(false); onOpenPlatformTools(); }}
            >
              <Wrench className="w-4 h-4" />
              Club Central tools
            </button>
          )}
          {isPlatformAdmin && (
            <a
              href="https://manage.clubcentral.app"
              target="_blank"
              rel="noopener noreferrer"
              className={menuItemCls}
              role="menuitem"
              onClick={() => setMenuOpen(false)}
            >
              <Building2 className="w-4 h-4" />
              ClubCentral Manager
              <ExternalLink className="w-3.5 h-3.5 ml-auto" />
            </a>
          )}
          <div className={`my-1 border-t ${getLightBorder(isDarkMode)}`} />
          <button className={menuItemCls} role="menuitem" onClick={() => { setMenuOpen(false); onLogout(); }}>
            <LogOut className="w-4 h-4" />
            Logout
          </button>
          {/* Platform attribution + build version, for support ("what
              version are you on?"). The version also shows at the foot of
              the side navigation. */}
          <div className={`px-3 pt-1.5 pb-0.5 text-[11px] select-text ${getMutedText(isDarkMode)}`} title={versionTitle}>
            Club Central{versionLabel ? ` · ${versionLabel}` : ""}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountMenu;
